import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Clock, ArrowLeft, Sparkles, MessageCircle, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { API_BASE as API } from "@/config";
import { GA } from "@/lib/analytics";
import confetti from "canvas-confetti";
import axios from "axios";
import { PremiumNudge } from "@/components/PremiumNudge";
import { SessionTimer } from "@/components/SessionTimer";
import { ChatMessageList } from "@/components/ChatMessageList";

export default function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [endsAt, setEndsAt] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(180);
  const [sessionActive, setSessionActive] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [recharging, setRecharging] = useState(false);
  const [nudgeSeen, setNudgeSeen] = useState(false);
  const [initialCount, setInitialCount] = useState(null);
  const mainRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const el = mainRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: initialCount === null ? "auto" : "smooth" });
  }, [messages.length, sending, initialLoading]); // eslint-disable-line

  const fetchSession = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/sessions/${sessionId}/status`),
        axios.get(`${API}/chat/history/${sessionId}`),
      ]);
      const st = statusRes.data;
      const msgs = historyRes.data.messages || [];
      setSessionData(st);
      setMessages(msgs);
      setInitialCount(c => (c === null ? msgs.length : c));
      setSessionActive(st.chat_active);
      setSessionDuration(st.session_duration || 180);
      setEndsAt(st.time_remaining ? Date.now() + st.time_remaining * 1000 : null);
      if (!st.chat_active && st.payment_status === "paid") setSessionExpired(true);
    } catch { toast.error("Failed to load session"); }
    finally { setInitialLoading(false); }
  }, [sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const handleExpire = useCallback(() => { setSessionActive(false); setSessionExpired(true); }, []);

  const isBasic = sessionData?.current_plan === "basic";
  const premiumEnded = sessionExpired && sessionData && !isBasic;

  // Premium time over -> open the full unlocked report page (same report, PDF download)
  useEffect(() => {
    if (!premiumEnded) return;
    toast.success("Session complete! Opening your full Premium report...");
    const id = setTimeout(() => navigate(`/reading?session=${sessionId}`), 1800);
    return () => clearTimeout(id);
  }, [premiumEnded, sessionId, navigate]);

  const handleSend = async () => {
    if (!inputValue.trim() || sending || !sessionActive) return;
    const userMsg = { role: "user", content: inputValue.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/send`, { session_id: sessionId, message: userMsg.content, language });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.content, timestamp: res.data.timestamp }]);
    } catch (err) {
      if (err.response?.status === 403) handleExpire();
      else toast.error("Failed to get response.");
    } finally { setSending(false); inputRef.current?.focus({ preventScroll: true }); }
  };

  const handleUpgrade = async () => {
    setRecharging(true);
    setSessionExpired(false);
    GA.rechargeInitiated();
    try {
      const orderRes = await axios.post(`${API}/sessions/${sessionId}/recharge`);
      const orderData = orderRes.data;
      const options = {
        key: orderData.razorpay_key_id, amount: orderData.amount, currency: orderData.currency,
        name: "Vishu Astro",
        description: "Upgrade to Premium — ₹150 (10 min total)",
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/sessions/${sessionId}/recharge-verify`, {
              session_id: sessionId, razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature,
            });
            toast.success("👑 Premium Unlocked! All features + 10 min total chat");
            GA.rechargeSuccess();
            confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 }, colors: ['#FFC107', '#FFB300', '#FF9800'] });
            setSessionExpired(false);
            await fetchSession();
            setRecharging(false);
          } catch { toast.error("Payment verification failed."); setSessionExpired(true); setRecharging(false); }
        },
        modal: { ondismiss: () => { setSessionExpired(true); setRecharging(false); } },
        theme: { color: "#FFC107" },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { toast.error("Payment Failed."); setSessionExpired(true); setRecharging(false); });
      rzp.open();
    } catch (err) {
      if (err.response?.status === 409) { toast.info("Already Premium"); await fetchSession(); }
      else { toast.error("Something went wrong."); setSessionExpired(true); }
      setRecharging(false);
    }
  };

  const showNudge = sessionExpired && isBasic && !nudgeSeen;

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="text-center">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 w-20 h-20 rounded-full bg-amber-300 blur-xl opacity-40 animate-pulse-glow" style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }} />
            <img src="/logo.jpg" alt="" className="w-20 h-20 rounded-full object-cover mx-auto animate-float shadow-xl relative z-10 border-4 border-white" />
          </div>
          <p className="text-gray-900 font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</p>
          <p className="text-gray-400 text-xs">{t("connecting")}</p>
          <div className="flex gap-1.5 justify-center mt-4">
            <span className="w-2 h-2 rounded-full bg-amber-400 typing-dot" />
            <span className="w-2 h-2 rounded-full bg-amber-400 typing-dot" />
            <span className="w-2 h-2 rounded-full bg-amber-400 typing-dot" />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-gradient-to-b from-amber-50/30 to-white flex flex-col overflow-hidden" data-testid="chat-page">
      {/* Header */}
      <header className="relative shrink-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100 px-4 pt-3 pb-4" data-testid="chat-header">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-btn" onClick={() => navigate("/")} className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src="/logo.jpg" alt="" width="40" height="40" className="w-10 h-10 rounded-full object-cover shadow-sm" />
            <div>
              <p className="font-bold text-sm text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</p>
              <p className={`text-xs font-medium ${sessionActive ? "text-green-500" : "text-gray-400"}`}>
                {sessionActive ? (
                  <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />{t("chatOnline")}</span>
                ) : t("chatEnded")}
              </p>
            </div>
          </div>
          <SessionTimer endsAt={sessionActive ? endsAt : null} duration={sessionDuration} onExpire={handleExpire} />
        </div>
      </header>

      {/* Messages */}
      <main ref={mainRef} className="flex-1 overflow-y-auto overscroll-contain px-4 py-6" style={{ WebkitOverflowScrolling: "touch" }} data-testid="chat-messages-area">
        <div className="max-w-2xl mx-auto space-y-3">
          <ChatMessageList messages={messages} initialCount={initialCount ?? 0} sending={sending} />
        </div>
      </main>

      {/* Input */}
      <footer className="shrink-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 px-4 py-3" data-testid="chat-input-area">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <input data-testid="chat-input" ref={inputRef}
            placeholder={sessionActive ? t("chatPlaceholder") : t("chatExpiredPlaceholder")}
            value={inputValue} onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            disabled={!sessionActive || sending}
            className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-5 py-3 text-base sm:text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-colors disabled:opacity-50" />
          <button data-testid="send-btn" onClick={handleSend} disabled={!inputValue.trim() || sending || !sessionActive}
            className="w-11 h-11 shrink-0 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </footer>

      {/* Premium Upgrade Dialog */}
      <PremiumNudge open={showNudge} name={sessionData?.name} onReveal={() => setNudgeSeen(true)} />

      <Dialog open={sessionExpired && isBasic && !showNudge} onOpenChange={() => {}}>
        <DialogContent className="bg-white border-0 max-w-sm mx-auto rounded-3xl shadow-2xl p-0 overflow-hidden [&>button]:hidden" data-testid="session-expired-dialog">
          {/* Gradient header */}
          <div className="bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 px-6 pt-8 pb-6 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
            <div className="relative z-10">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 shadow-lg">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  {t("sessionEndedTitle")}
                </DialogTitle>
                <DialogDescription className="text-white/90 text-sm mt-2 leading-relaxed">
                  {t("sessionEndedDesc")}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>

          {/* Features & CTA */}
          <div className="px-6 pb-6 pt-5">
            {/* Price badge */}
                <div className="flex items-center justify-center gap-3 mb-5">
                  <span className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>₹150</span>
                  <span className="text-gray-400 text-sm">=</span>
                  <span className="text-amber-600 font-semibold text-sm">Premium Upgrade (10 min total)</span>
                </div>
                <div className="space-y-3 mb-6">
                  {[
                    { icon: <Star className="w-4 h-4" />, text: "Everything in Basic unlocked" },
                    { icon: <Zap className="w-4 h-4" />, text: "Money Secret, Numerology, 5-Year Timeline" },
                    { icon: <MessageCircle className="w-4 h-4" />, text: "7 more minutes of AI chat (10 min total)" },
                    { icon: <Clock className="w-4 h-4" />, text: "Downloadable PDF Report" },
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 flex-shrink-0">{f.icon}</div>
                      <span>{f.text}</span>
                    </div>
                  ))}
                </div>

            <Button data-testid="recharge-btn" onClick={handleUpgrade} disabled={recharging} className="btn-primary w-full py-4 text-base rounded-2xl">
              {recharging ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />Processing...</span>
              ) : (
                <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> 👑 Upgrade to Premium — ₹150</span>
              )}
            </Button>
            <Button data-testid="back-to-home-btn" variant="ghost" onClick={() => navigate("/")} className="w-full mt-2 text-gray-400 hover:text-gray-700 text-sm">
              {t("backHome")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
